import React, { FC, useCallback, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useDispatch } from 'react-redux';
import translate from '@translate';
import Button from 'src/components/Button';
import IconButton from 'src/components/IconButton';
import Typography from 'src/components/Typography';
import { addMessage } from 'src/resources/alertMessages/reducer';
import { formatJsonForDisplay, isJsonString } from 'src/containers/WebhookRecordsListContainer/functions';
import { parseJsonValue, renderJsonHighlight } from './renderJsonHighlight';

type JsonCodeViewerProps = {
    value: string;
    label?: string;
    helperText?: string;
    testId?: string;
    minHeight?: number;
    showFormatToggle?: boolean;
};

const JsonCodeViewer: FC<JsonCodeViewerProps> = (props) => {
    const {
        value,
        label,
        helperText,
        testId,
        minHeight = 160,
        showFormatToggle = true,
    } = props;
    const dispatch = useDispatch();
    const [showFormatted, setShowFormatted] = useState(true);

    const canFormat = useMemo(() => isJsonString(value), [value]);
    const formattedValue = useMemo(() => formatJsonForDisplay(value), [value]);
    const parsedValue = useMemo(() => parseJsonValue(value), [value]);
    const displayRawValue = showFormatted && canFormat ? formattedValue : value;
    const showHighlight = showFormatted && canFormat && parsedValue !== null;

    const handleCopy = useCallback(async () => {
        if (!displayRawValue || !navigator?.clipboard) {
            return;
        }
        try {
            await navigator.clipboard.writeText(displayRawValue);
            dispatch(
                addMessage({
                    type: 'success',
                    title: translate('messages.copiedToClipboard'),
                }),
            );
        } catch {
            dispatch(
                addMessage({
                    type: 'error',
                    title: translate('messages.copyFailed'),
                }),
            );
        }
    }, [dispatch, displayRawValue]);

    const showToolbar = Boolean(label) || (showFormatToggle && canFormat) || Boolean(displayRawValue);

    return (
        <Box>
            {showToolbar && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        mb: 1,
                    }}
                >
                    {label ? (
                        <Typography variant="caption" display="block">
                            {label}
                        </Typography>
                    ) : (
                        <span />
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {showFormatToggle && canFormat && (
                            <Button
                                variant="text"
                                size="small"
                                onClick={() => setShowFormatted((current) => !current)}
                                data-testid={testId ? `${testId}-format-toggle` : undefined}
                            >
                                {showFormatted
                                    ? translate('webhookRecordDetail.payload.showRaw')
                                    : translate('webhookRecordDetail.payload.format')}
                            </Button>
                        )}
                        <IconButton
                            size="small"
                            tooltiop={translate('webhookRecordDetail.action.copy')}
                            onClick={handleCopy}
                            disabled={!displayRawValue}
                            data-testid={testId ? `${testId}-copy` : undefined}
                            sx={{ padding: '4px' }}
                        >
                            <ContentCopyIcon sx={{ fontSize: '18px' }} />
                        </IconButton>
                    </Box>
                </Box>
            )}
            <Box
                component="pre"
                data-testid={testId}
                sx={{
                    m: 0,
                    p: 2,
                    minHeight,
                    maxHeight: 480,
                    overflow: 'auto',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: '#f8f9fa',
                    fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                    fontSize: '0.875rem',
                    lineHeight: 1.5,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                }}
            >
                {showHighlight ? renderJsonHighlight(parsedValue) : displayRawValue}
            </Box>
            {helperText && (
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    {helperText}
                </Typography>
            )}
            {!canFormat && (
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    {translate('webhookRecordDetail.payload.notJson')}
                </Typography>
            )}
        </Box>
    );
};

export default JsonCodeViewer;
